import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

const ACTIVATION_KEYS = ["Enter", " ", "Spacebar"];
const SPACE_KEYS = [" ", "Spacebar"];

const defaultNow = () =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

const requestFrame = (callback) =>
  typeof window !== "undefined" &&
  typeof window.requestAnimationFrame === "function"
    ? window.requestAnimationFrame(callback)
    : setTimeout(callback, 16);

const cancelFrame = (handle) => {
  if (handle === null || handle === undefined) return;
  if (
    typeof window !== "undefined" &&
    typeof window.cancelAnimationFrame === "function"
  ) {
    window.cancelAnimationFrame(handle);
  }
  clearTimeout(handle);
};

export function useActivationGuard({
  resetKey,
  open = true,
  graceMs = 500,
  now = defaultNow,
} = {}) {
  const armedAtRef = useRef(null);
  const frameRef = useRef(null);
  const freshKeyTargetRef = useRef(null);
  const nowRef = useRef(now);
  const graceRef = useRef(graceMs);
  nowRef.current = now;
  graceRef.current = graceMs;

  const rearm = useCallback(() => {
    armedAtRef.current = null;
    freshKeyTargetRef.current = null;
    cancelFrame(frameRef.current);
    frameRef.current = requestFrame(() => {
      frameRef.current = requestFrame(() => {
        frameRef.current = null;
        armedAtRef.current = nowRef.current();
      });
    });
  }, []);

  useLayoutEffect(() => {
    if (open) rearm();
  }, [resetKey, open, rearm]);

  useEffect(() => () => cancelFrame(frameRef.current), []);

  useEffect(() => {
    if (!open) return undefined;
    const onWindowFocus = () => rearm();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") rearm();
    };
    window.addEventListener("focus", onWindowFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("focus", onWindowFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [open, rearm]);

  const isBlocked = useCallback((event) => {
    const armedAt = armedAtRef.current;
    if (armedAt === null) return true;
    const current = nowRef.current();
    const stamp = event?.timeStamp;
    const eventTime =
      Number.isFinite(stamp) && stamp > 0 && stamp <= current + 1000
        ? stamp
        : current;
    return eventTime - armedAt < graceRef.current;
  }, []);

  const onKeyDownCapture = useCallback(
    (event) => {
      if (!ACTIVATION_KEYS.includes(event.key)) return;
      if (event.repeat || isBlocked(event)) {
        event.preventDefault();
        freshKeyTargetRef.current = null;
        return;
      }
      freshKeyTargetRef.current = event.target;
    },
    [isBlocked],
  );

  const onKeyUpCapture = useCallback((event) => {
    if (!SPACE_KEYS.includes(event.key)) return;
    if (freshKeyTargetRef.current !== event.target) event.preventDefault();
    freshKeyTargetRef.current = null;
  }, []);

  const guard = useCallback(
    (handler) => (event) => {
      if (isBlocked(event)) {
        event?.preventDefault?.();
        return;
      }
      handler(event);
    },
    [isBlocked],
  );

  return { onKeyDownCapture, onKeyUpCapture, guard, rearm, isBlocked };
}
